import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';
import MarketingHeader from '@/components/layout/MarketingHeader';
import MarketingFooter from '@/components/layout/MarketingFooter';
import FAQ, { generateIndustryFAQs } from '@/components/FAQ';
import { 
  Star, 
  Sparkles, 
  ArrowRight,
  Check,
  MapPin,
  TrendingUp,
  Shield,
  Target,
  Search,
  BadgeCheck,
  Quote
} from 'lucide-react';

export interface CaseStudy {
  businessType: string;
  city: string;
  challenges: string[];
  solution: string;
  results: {
    metric: string;
    value: string;
  }[];
}

export interface SampleReview {
  category: string;
  reviews: {
    text: string;
    keywords: string[];
  }[];
}

export interface IndustryChallenge {
  title: string;
  description: string;
}

export interface UseCase {
  category: string;
  description: string;
  keywords: string[];
}

export interface IndustryPageData {
  slug: string;
  name: string;
  heroHeadline: string;
  heroSubheadline: string;
  painPoint: string;
  trustProblem: string;
  challenges: IndustryChallenge[];
  useCases: UseCase[];
  caseStudy: CaseStudy;
  sampleReviews: SampleReview[];
  cityKeywords: string[];
  nearMeKeywords: string[];
  ctaText: string;
}

interface Props {
  data: IndustryPageData;
}

export default function IndustryLandingTemplate({ data }: Props) {
  const navigate = useNavigate();

  const baseUrl = 'https://reviewplum.com';
  const pageTitle = `${data.name} Review Management | Review Plum - Boost Google Reviews`;
  const metaDescription = `${data.heroSubheadline} ${data.painPoint}. Get more 5-star Google reviews for your ${data.name.toLowerCase()} business in India.`;
  const canonicalUrl = `${baseUrl}/industries/${data.slug}`;
  
  const keywords = [
    data.name.toLowerCase(),
    'google reviews',
    'review management',
    'local seo',
    ...data.cityKeywords.slice(0, 5),
    ...data.nearMeKeywords.slice(0, 3)
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${canonicalUrl}#webpage`,
        'url': canonicalUrl,
        'name': pageTitle,
        'description': metaDescription,
        'isPartOf': { '@id': `${baseUrl}#website` },
        'about': { '@id': `${canonicalUrl}#service` },
        'breadcrumb': { '@id': `${canonicalUrl}#breadcrumb` }
      },
      {
        '@type': 'Service',
        '@id': `${canonicalUrl}#service`,
        'name': `Review Plum for ${data.name}`,
        'description': data.heroSubheadline,
        'provider': { '@type': 'Organization', 'name': 'Review Plum', 'url': baseUrl },
        'areaServed': { '@type': 'Country', 'name': 'India' },
        'serviceType': 'Review Management Software',
        'audience': { '@type': 'Audience', 'audienceType': data.name }
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonicalUrl}#breadcrumb`,
        'itemListElement': [
          { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': baseUrl },
          { '@type': 'ListItem', 'position': 2, 'name': data.name, 'item': canonicalUrl }
        ]
      },
      {
        '@type': 'FAQPage',
        '@id': `${canonicalUrl}#faq`,
        'mainEntity': data.challenges.map((challenge) => ({
          '@type': 'Question',
          'name': `What is the "${challenge.title}" challenge for ${data.name}?`,
          'acceptedAnswer': { '@type': 'Answer', 'text': challenge.description }
        }))
      },
      {
        '@type': 'Article',
        '@id': `${canonicalUrl}#casestudy`,
        'headline': `How a ${data.caseStudy.businessType} in ${data.caseStudy.city} Improved Their Google Reviews`,
        'description': data.caseStudy.solution,
        'author': { '@type': 'Organization', 'name': 'Review Plum' },
        'publisher': { '@type': 'Organization', 'name': 'Review Plum', 'url': baseUrl }
      }
    ]
  };

  const solutions = [
    {
      icon: Target,
      title: 'Category-Based Review Generation',
      description: `Generate reviews tailored to specific ${data.name.toLowerCase()} services and specializations.`,
    },
    {
      icon: Search,
      title: 'Local SEO Keyword Injection',
      description: 'Automatically embed city names, locality keywords, and service terms into reviews.',
    },
    {
      icon: Shield,
      title: 'Manual Google-Safe Posting',
      description: 'Customers copy and post reviews themselves - 100% compliant with Google policies.',
    },
    {
      icon: Sparkles,
      title: 'Review Variation Engine',
      description: 'Generate unique, authentic-sounding reviews every time to avoid detection.',
    },
  ];

  // Generate industry-specific FAQs
  const industryFAQs = generateIndustryFAQs(data.name, data.painPoint);

  return (
    <div className="min-h-screen bg-background">
      {/* SEO Component */}
      <SEO
        title={pageTitle}
        description={metaDescription}
        keywords={keywords}
        canonicalUrl={canonicalUrl}
        jsonLd={jsonLd}
      />

      {/* Navigation */}
      <MarketingHeader />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              For {data.name} in India
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-6">
              {data.heroHeadline}
            </h1>
            
            <p className="text-lg sm:text-xl text-muted-foreground mb-4 max-w-2xl mx-auto">
              {data.heroSubheadline}
            </p>

            <div className="bg-destructive/10 text-destructive rounded-xl p-4 max-w-xl mx-auto mb-8">
              <p className="text-sm font-medium">{data.painPoint}</p>
              <p className="text-xs mt-1 text-destructive/80">{data.trustProblem}</p>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                onClick={() => navigate('/login')}
                className="gradient-btn h-14 px-8 text-lg"
                size="lg"
              >
                {data.ctaText}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-6 mt-10 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-success" />
                100% Google-Compliant
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-success" />
                No Fake Reviews
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-success" />
                Setup in 2 Minutes
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Challenges Section */}
      <section className="py-16 lg:py-24 bg-accent/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
              Challenges Facing {data.name} Today
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Most {data.name.toLowerCase()} struggle with building their online reputation on Google Maps.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.challenges.map((challenge, index) => (
              <div 
                key={index}
                className="soft-card border-destructive/20 bg-destructive/5"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-destructive font-bold">{index + 1}</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">{challenge.title}</h3>
                    <p className="text-muted-foreground text-sm">{challenge.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How Review Plum Helps */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
              How Review Plum Helps {data.name}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A complete solution designed specifically for {data.name.toLowerCase()} to dominate local search.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {solutions.map((solution, index) => (
              <div 
                key={index}
                className="soft-card group hover:border-primary/30 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <solution.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">{solution.title}</h3>
                <p className="text-muted-foreground text-sm">{solution.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service-Level Use Cases */}
      <section className="py-16 lg:py-24 bg-accent/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
              Review Categories for {data.name}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Generate targeted reviews for each service you offer.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {data.useCases.map((useCase, index) => (
              <div 
                key={index}
                className="soft-card"
              >
                <div className="flex items-center gap-2 mb-3">
                  <BadgeCheck className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-foreground">{useCase.category}</h3>
                </div>
                <p className="text-muted-foreground text-sm mb-4">{useCase.description}</p>
                <div className="flex flex-wrap gap-2">
                  {useCase.keywords.map((keyword, i) => (
                    <span 
                      key={i}
                      className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-lg"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
              Success Story
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See how a {data.caseStudy.businessType} in {data.caseStudy.city} transformed their online presence.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="soft-card bg-gradient-to-br from-card to-accent/30">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Challenges */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin className="w-5 h-5 text-primary" />
                    <span className="font-semibold">{data.caseStudy.businessType} • {data.caseStudy.city}</span>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-foreground mb-3">The Challenge</h3>
                  <ul className="space-y-2 mb-6">
                    {data.caseStudy.challenges.map((challenge, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <span className="w-1.5 h-1.5 rounded-full bg-destructive mt-2 flex-shrink-0" />
                        {challenge}
                      </li>
                    ))}
                  </ul>

                  <h3 className="text-lg font-semibold text-foreground mb-3">The Solution</h3>
                  <p className="text-sm text-muted-foreground">{data.caseStudy.solution}</p>
                </div>

                {/* Results */}
                <div className="bg-primary/5 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    Results After 3 Months
                  </h3>
                  <div className="space-y-4">
                    {data.caseStudy.results.map((result, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-background rounded-lg">
                        <span className="text-sm text-muted-foreground">{result.metric}</span>
                        <span className="text-lg font-bold text-primary">{result.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sample Reviews Section */}
      <section className="py-16 lg:py-24 bg-accent/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
              Sample SEO-Optimized Reviews
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See the kind of reviews Review Plum generates for {data.name.toLowerCase()}.
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {data.sampleReviews.map((category, index) => (
              <span 
                key={index}
                className={`px-4 py-2 rounded-xl text-sm font-medium ${
                  index === 0 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {category.category}
              </span>
            ))}
          </div>

          {/* Reviews Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {data.sampleReviews[0]?.reviews.map((review, index) => (
              <div key={index} className="soft-card">
                <div className="flex gap-1 mb-3">
                  {[1,2,3,4,5].map((star) => (
                    <Star key={star} className="w-4 h-4 fill-warning text-warning" />
                  ))}
                </div>
                <Quote className="w-8 h-8 text-primary/20 mb-2" />
                <p className="text-sm text-foreground mb-4 leading-relaxed">
                  {review.text}
                </p>
                <div className="flex flex-wrap gap-2">
                  {review.keywords.map((keyword, i) => (
                    <span 
                      key={i}
                      className="px-2 py-1 bg-success/10 text-success text-xs rounded-lg font-medium"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Keyword Coverage Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
              Local SEO Keyword Coverage
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Target high-intent local keywords to boost your Google Maps visibility.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* City + Service Keywords */}
            <div className="soft-card">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-foreground">City + Service Keywords</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {data.cityKeywords.map((keyword, i) => (
                  <span 
                    key={i}
                    className="px-3 py-1.5 bg-primary/10 text-primary text-sm rounded-lg"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>

            {/* Near Me Keywords */}
            <div className="soft-card">
              <div className="flex items-center gap-2 mb-4">
                <Search className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-foreground">"Near Me" Intent Keywords</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {data.nearMeKeywords.map((keyword, i) => (
                  <span 
                    key={i}
                    className="px-3 py-1.5 bg-accent text-accent-foreground text-sm rounded-lg"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-primary/10 via-accent/20 to-primary/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
            Ready to Dominate Local Search?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join hundreds of {data.name.toLowerCase()} across India using Review Plum to grow their Google reviews and local visibility.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              onClick={() => navigate('/login')}
              className="gradient-btn h-14 px-8 text-lg"
              size="lg"
            >
              Get Started Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              variant="outline"
              className="h-14 px-8 text-lg rounded-xl"
              size="lg"
              onClick={() => navigate('/')}
            >
              Learn More
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-6">
            Plans start at ₹5,000/year
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        faqs={industryFAQs}
        title={`${data.name} FAQ`}
        subtitle={`Common questions about Review Plum for ${data.name.toLowerCase()}`}
        industryContext={data.name}
      />

      {/* Footer */}
      <MarketingFooter />
    </div>
  );
}
